import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mic, MicOff, Send, Phone, Check, Droplets, Zap, Loader2,
  MessageSquare, Package, User, MapPin, ArrowRight, Building, Tractor,
  Home, Waves, Globe, ChevronLeft, Shield, HelpCircle, Sparkles,
  ArrowDown, ArrowUp, Circle, CloudRain, Thermometer, Trash2, Gauge, Container,
  RotateCcw, Lightbulb, Star, CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Uzhavar Maiyyam Design Tokens - with teal accent
const T = { 
  red: "#B22222",      // Primary brand color
  redDark: "#8B1A1A",  // Darker red
  teal: "#009b94",     // Teal accent (keep some)
  tealDk: "#007a75",   // Darker teal
  tealLt: "#e6f7f6",   // Light teal bg
  cream: "#F5F1EA",    // Background
  warm: "#EDEAD4",     // Warm background
  ink: "#1A1714",      // Primary text
  ink2: "#3D3630",     // Secondary text
  ink3: "#7A6F66",     // Muted text
  border: "#D5CCBC",   // Border color
  white: "#ffffff",
  wa: "#25D366",       // WhatsApp green
};

// Logo URLs
const LOGO_ROUND = "https://customer-assets.emergentagent.com/job_73cc6856-0552-4c52-8622-148bde206541/artifacts/9pyswqop_UM%20LOGO%20%282%29.png";
const LOGO_NAME = "https://customer-assets.emergentagent.com/job_73cc6856-0552-4c52-8622-148bde206541/artifacts/q36seewn_UM%20Name%20Font%20-%20RED.JPG";
const LOGO_TEXMO = "https://customer-assets.emergentagent.com/job_73cc6856-0552-4c52-8622-148bde206541/artifacts/jzxr8nmn_image.png";

const ICONS = {
  home: Home, gauge: Gauge, tractor: Tractor, container: Container,
  waves: Waves, "trash-2": Trash2, "help-circle": HelpCircle, zap: Zap,
  "arrow-down": ArrowDown, "arrow-up": ArrowUp, circle: Circle, check: Check,
  "map-pin": MapPin, droplets: Droplets, "cloud-rain": CloudRain,
  thermometer: Thermometer, sparkles: Sparkles, building: Building, user: User,
  star: Star, "check-circle": CheckCircle,
};

const getIcon = (n, s = 18) => { const C = ICONS[n]; return C ? <C size={s} /> : null; };

const CAT_IMAGES = {};

const slide = {
  enter: { opacity: 0, y: 12 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

// Phone validation — must be exactly 10 digits
const PHONE_REGEX = /^\d{10}$/;
const isValidPhone = (p) => PHONE_REGEX.test((p || "").trim());

// =============================================
// THINKING INDICATOR
// =============================================
function ThinkingIndicator({ language }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-10 gap-3">
      <div className="flex gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full think-dot" style={{ backgroundColor: T.red }} />
        <span className="w-2.5 h-2.5 rounded-full think-dot" style={{ backgroundColor: T.red }} />
        <span className="w-2.5 h-2.5 rounded-full think-dot" style={{ backgroundColor: T.red }} />
      </div>
      <span className="text-sm font-medium" style={{ color: T.ink3 }}>
        {language === "ta" ? "ஆராய்கிறது..." : "Finding the right pump..."}
      </span>
    </motion.div>
  );
}

// =============================================
// ENTRY SCREEN - Uzhavar Maiyyam Design
// =============================================
function EntryScreen({ dealer, language, setLanguage, onStart, speechSupported, onStartVoice }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: T.cream }}>
      {/* Header */}
      <header className="px-4 sm:px-6 py-3 border-b sticky top-0 z-10 backdrop-blur-md" style={{ backgroundColor: 'rgba(255,255,255,0.95)', borderColor: T.border }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_ROUND} alt="உழவர் மையம்" className="h-11 w-11 object-contain" />
            <img src={LOGO_NAME} alt="உழவர் மையம்" className="h-8 object-contain" />
          </div>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="text-sm px-3 py-1.5 rounded-lg border focus:outline-none focus:ring-2"
            style={{ borderColor: T.border, color: T.ink2 }}
            data-testid="language-select"
          >
            <option value="en">English</option>
            <option value="ta">தமிழ்</option>
          </select>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }}
          className="max-w-lg w-full text-center"
        >
          {/* Main Title */}
          <h2 className="text-3xl sm:text-4xl mb-4 font-semibold" style={{ color: T.ink, lineHeight: 1.2 }}>
            Not sure which pump<br/>
            <span style={{ color: T.red }}>is right for you?</span>
          </h2>
          
          {/* Subtitle */}
          <p className="text-base mb-8" style={{ color: T.ink2, lineHeight: 1.6 }}>
            Answer a few simple questions and get the right pump recommendation — backed by six decades of field knowledge.
          </p>

          {/* CTA Button - use teal */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onStart}
            className="w-full sm:w-auto px-8 py-4 rounded-xl font-semibold text-white shadow-lg transition-all flex items-center justify-center gap-2 mx-auto"
            style={{ backgroundColor: T.teal }}
            data-testid="get-started-btn"
          >
            <span>Find My Pump</span>
            <ArrowRight size={18} />
          </motion.button>
        </motion.div>
      </main>

      {/* Footer with Texmo logo */}
      <footer className="px-4 py-4 text-center border-t" style={{ borderColor: T.border, backgroundColor: T.white }}>
        <img src={LOGO_TEXMO} alt="Texmo Authorized Dealer" className="h-8 object-contain mx-auto mb-2 opacity-70" />
        <p className="text-xs" style={{ color: T.ink3 }}>உழவர் மையம் · Thanjavur · Since 1966</p>
      </footer>
    </div>
  );
}

// =============================================
// PROGRESS BAR
// =============================================
function ProgressBar({ pct = 0, label = "" }) {
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-medium" style={{ color: T.ink3 }}>{label}</span>
        <span className="text-[11px] font-bold" style={{ color: T.teal }}>{pct}%</span>
      </div>
      <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: T.border }}>
        <motion.div className="h-full rounded-full" style={{ backgroundColor: T.teal }}
          initial={false} animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: "easeOut" }} />
      </div>
    </div>
  );
}

// =============================================
// PILLS
// =============================================
function Pills({ reqs }) {
  const entries = Object.entries(reqs || {});
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mb-4" data-testid="requirements-pills">
      {entries.map(([k, v]) => (
        <div key={k} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-[11px]">
          <span className="text-slate-400">{k}:</span>
          <span className="font-semibold text-slate-700">{v}</span>
        </div>
      ))}
    </div>
  );
}

// =============================================
// CATEGORY SELECTION (with images) - Uzhavar Design
// =============================================
function CategorySelection({ question, onSelect, disabled }) {
  const [sel, setSel] = useState(null);
  if (!question) return null;

  const handle = (qid, val) => {
    setSel(val);
    setTimeout(() => onSelect(qid, val), 250);
  };

  return (
    <motion.div variants={slide} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
      <div className="mb-5">
        <h3 className="text-xl font-semibold mb-1" style={{ color: T.ink }} data-testid="question-text">{question.text}</h3>
        {question.reasoning && <p className="text-sm" style={{ color: T.ink3 }}>{question.reasoning}</p>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {question.options?.map((opt, i) => {
          const img = CAT_IMAGES[opt.value];
          return (
            <motion.button key={opt.value}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06, duration: 0.25 }}
              onClick={() => handle(question.id, opt.value)} disabled={disabled}
              data-testid={`option-${opt.value}`}
              className={`relative overflow-hidden rounded-xl border-2 text-left transition-all group ${
                sel === opt.value ? "scale-[0.98]" : "hover:shadow-md"
              }`}
              style={{ 
                borderColor: sel === opt.value ? T.teal : (opt.popular ? T.red : T.border),
                backgroundColor: sel === opt.value ? T.tealLt : T.white
              }}>
              {opt.popular && (
                <div className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow" style={{ backgroundColor: T.red }}>
                  Most popular
                </div>
              )}
              {img && (
                <div className="h-24 sm:h-28 overflow-hidden">
                  <img src={img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/90" style={{ top: "40%" }} />
                </div>
              )}
              <div className={`flex items-center gap-3 px-4 ${img ? "pb-4 pt-1 -mt-3 relative" : "py-4"}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                  style={{ 
                    backgroundColor: sel === opt.value ? T.tealLt : T.cream,
                    color: sel === opt.value ? T.teal : T.ink2
                  }}>
                  {getIcon(opt.icon, 18) || <Circle size={18} />}
                </div>
                <span className="text-sm font-semibold" style={{ color: T.ink }}>{opt.label}</span>
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}

// =============================================
// QUESTION CARD - Uzhavar Design
// =============================================
function QuestionCard({ question, reasoning, onSelect, disabled }) {
  const [sel, setSel] = useState(null);
  const [textInput, setTextInput] = useState('');
  if (!question) return null;

  const handle = (qid, val) => {
    setSel(val);
    setTimeout(() => onSelect(qid, val), 200);
  };

  const handleTextSubmit = () => {
    if (textInput.trim()) {
      handle(question.id, textInput.trim());
    }
  };

  const handleSkip = () => {
    handle(question.id, 'skip');
  };

  const hasDesc = question.options?.some((o) => o.desc);
  const isTextInput = question.input_type === 'text_optional';

  return (
    <motion.div variants={slide} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }}>
      {reasoning && (
        <div className="mb-4 px-4 py-3 rounded-xl" style={{ backgroundColor: T.warm, border: `1px solid ${T.border}` }}>
          <p className="text-sm italic leading-relaxed" style={{ color: T.ink2 }}>
            <Lightbulb size={13} className="inline mr-1.5" style={{ color: T.red }} />{reasoning}
          </p>
        </div>
      )}
      <div className="rounded-xl p-5 sm:p-6 shadow-sm" style={{ backgroundColor: T.white, border: `1px solid ${T.border}` }}>
        <h3 className="text-lg font-semibold mb-1" style={{ color: T.ink }} data-testid="question-text">{question.text}</h3>
        {question.reasoning && <p className="text-sm mb-5" style={{ color: T.ink3 }}>{question.reasoning}</p>}
        
        {isTextInput ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder={question.placeholder || 'Type here...'}
                disabled={disabled}
                className="flex-1 px-4 py-3 rounded-lg border-2 focus:outline-none"
                style={{ borderColor: T.border, color: T.ink }}
                onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                data-testid="text-input"
              />
              <button
                onClick={handleTextSubmit}
                disabled={disabled || !textInput.trim()}
                className="px-5 py-3 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ backgroundColor: T.teal }}
                data-testid="text-submit"
              >
                <ArrowRight size={18} />
              </button>
            </div>
            {question.skip_label && (
              <button
                onClick={handleSkip}
                disabled={disabled}
                className="w-full px-4 py-2.5 text-sm rounded-lg transition-colors hover:bg-opacity-50"
                style={{ color: T.ink3 }}
                data-testid="skip-button"
              >
                {question.skip_label}
              </button>
            )}
          </div>
        ) : (
          <div className={`grid gap-2 ${hasDesc ? "sm:grid-cols-1" : "sm:grid-cols-2"}`}>
            {question.options?.map((opt, i) => (
              <motion.button key={opt.value}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.2 }}
                onClick={() => handle(question.id, opt.value)} disabled={disabled}
                data-testid={`option-${opt.value}`}
                className="flex items-center gap-3 px-4 py-3.5 rounded-lg border-2 text-left font-medium transition-all disabled:opacity-50 group"
                style={{ 
                  borderColor: sel === opt.value ? T.teal : T.border,
                  backgroundColor: sel === opt.value ? T.tealLt : T.white,
                  transform: sel === opt.value ? 'scale(0.98)' : 'scale(1)'
                }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                  style={{ 
                    backgroundColor: sel === opt.value ? T.tealLt : T.cream,
                    color: sel === opt.value ? T.teal : T.ink2
                  }}>
                  {getIcon(opt.icon, 18) || <Circle size={18} />}
                </div>
                <div className="min-w-0">
                  <span className="text-sm block" style={{ color: T.ink }}>{opt.label}</span>
                  {opt.desc && <span className="text-[11px] block mt-0.5" style={{ color: T.ink3 }}>{opt.desc}</span>}
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// =============================================
// FRAMING MESSAGE - Uzhavar Design
// =============================================
function Framing({ text }) {
  if (!text) return null;
  return (
    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
      className="mb-4 px-4 py-3 rounded-lg border-l-4" style={{ borderColor: T.teal, backgroundColor: T.tealLt }}>
      <p className="text-sm font-medium leading-relaxed" style={{ color: T.tealDk }}>{text}</p>
    </motion.div>
  );
}

// =============================================
// RECOMMENDATION - Uzhavar Design
// =============================================
function Recommendation({ step, dealer, onLeadSubmit, leadSubmitted, leadLoading, language, refNum, waUrl, scrollRef }) {
  const rec = step?.recommendation;
  const acc = step?.accessories;
  const p = rec?.primary;
  const alt = rec?.alternative;
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [loc, setLoc] = useState("");
  const [showFull, setShowFull] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");

  if (!p) return null;

  const phoneValid = isValidPhone(phone);
  const onPhoneChange = (v) => {
    // keep digits only, cap at 10
    const digits = (v || "").replace(/\D/g, "").slice(0, 10);
    setPhone(digits);
    if (phoneErr) setPhoneErr("");
  };
  const tryNext = () => {
    if (!phoneValid) { setPhoneErr("Please enter a valid 10-digit mobile number"); return; }
    setShowFull(true);
  };
  const trySubmit = () => {
    if (!phoneValid) { setPhoneErr("Please enter a valid 10-digit mobile number"); return; }
    if (!name.trim()) return;
    onLeadSubmit({ phone, name, location: loc });
  };

  const confMap = {
    high: { l: "Strong match", c: "#10b981", bg: "#ecfdf5" },
    good: { l: "Good match", c: T.red, bg: "#FEF2F2" },
    moderate: { l: "Expert confirmation needed", c: "#f59e0b", bg: "#fffbeb" },
  };
  const cf = confMap[rec.confidence] || confMap.good;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="space-y-4" data-testid="recommendation-view">
      {/* Scroll anchor at the very top of recommendation */}
      <div ref={scrollRef} style={{ marginTop: '-20px', paddingTop: '20px' }} />
      <p className="text-base font-medium" style={{ color: T.ink2 }}>{step.message}</p>

      {/* TOP 3 (hybrid LLM) OR single primary (fallback view) */}
      {Array.isArray(rec.top3) && rec.top3.length > 0 ? (
        <div className="space-y-4" data-testid="top3-section">
          {rec.top3.map((it, idx) => {
            const tp = it.pump || {};
            const isTop = idx === 0;
            return (
              <motion.div key={tp.id || idx}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + idx * 0.08 }}
                className="rounded-xl overflow-hidden shadow-md"
                style={{ border: `1px solid ${isTop ? T.red : T.border}`, boxShadow: isTop ? `0 6px 20px rgba(178,34,34,0.12)` : undefined }}
                data-testid={`top3-card-${idx}`}>
                <div className="px-4 sm:px-5 py-4" style={{ background: isTop ? `linear-gradient(135deg, ${T.ink}, #2D2620)` : "#ffffff", color: isTop ? "#ffffff" : T.ink }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <span className={`text-[10px] font-bold uppercase tracking-[0.18em] flex items-center gap-1.5 ${isTop ? "text-white/60" : ""}`} style={isTop ? undefined : { color: T.ink3 }}>
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold" style={{ backgroundColor: isTop ? "rgba(255,255,255,0.15)" : "#FEE2E2", color: isTop ? "#fff" : T.red }}>{idx + 1}</span>
                        {idx === 0 ? "Best Match" : idx === 1 ? "Strong Alternative" : "Also Consider"}
                      </span>
                      <h3 className={`${isTop ? "text-xl sm:text-2xl" : "text-lg"} font-bold mt-1`}>
                        {tp.brand} {tp.model || tp.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {tp.hp != null && <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${isTop ? "bg-white/10 text-white" : ""}`} style={isTop ? undefined : { backgroundColor: T.cream, color: T.ink }}>{tp.hp} HP</span>}
                        {tp.stages && <span className={`px-2 py-0.5 rounded-md text-xs ${isTop ? "bg-white/10 text-white/80" : ""}`} style={isTop ? undefined : { backgroundColor: T.cream, color: T.ink2 }}>{tp.stages} stg</span>}
                        {tp.phase && <span className={`px-2 py-0.5 rounded-md text-xs capitalize ${isTop ? "bg-white/10 text-white/80" : ""}`} style={isTop ? undefined : { backgroundColor: T.cream, color: T.ink2 }}>{tp.phase} phase</span>}
                      </div>
                    </div>
                    {tp.price != null && (
                      <div className={`text-right flex-shrink-0 ${isTop ? "" : ""}`}>
                        <div className={`${isTop ? "text-white" : ""} text-lg font-bold`} style={isTop ? undefined : { color: T.ink }}>{"\u20B9"}{Number(tp.price).toLocaleString("en-IN")}</div>
                        <div className={`text-[10px] ${isTop ? "text-white/60" : ""}`} style={isTop ? undefined : { color: T.ink3 }}>approx.</div>
                      </div>
                    )}
                  </div>
                </div>
                {/* LLM / fallback explanation box — brand cream + red accent */}
                {it.reason && (
                  <div className="px-4 sm:px-5 py-3" style={{ backgroundColor: "#F5F1EA", borderTop: `3px solid #B22222` }} data-testid={`top3-reason-${idx}`}>
                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: "#B22222" }}>
                        <Check size={11} style={{ color: "#ffffff" }} strokeWidth={3} />
                      </div>
                      <p className="text-[13px] leading-relaxed" style={{ color: "#3D3630" }}>
                        {it.reason}
                      </p>
                    </div>
                  </div>
                )}
                {/* Why this pump (rule engine bullets) for rank 1 only */}
                {isTop && rec.why?.length > 0 && (
                  <div className="px-4 sm:px-5 py-3 border-t" style={{ backgroundColor: T.white, borderColor: T.border }}>
                    <h4 className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: T.ink3 }}>Spec match</h4>
                    <ul className="space-y-1.5">
                      {rec.why.slice(0, 3).map((r, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs" style={{ color: T.ink2 }}>
                          <Check size={12} style={{ color: T.red }} className="mt-0.5 flex-shrink-0" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            );
          })}
          {rec.top3_source === "llm" && (
            <p className="text-[10px] text-center opacity-50" style={{ color: T.ink3 }} data-testid="llm-badge">
              AI-ranked from {rec.top3.length} best matches in our catalog
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Fallback: single primary card (no top3 available) */}
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15, duration: 0.35 }}
            className="rounded-xl overflow-hidden shadow-lg" style={{ border: `1px solid ${T.border}` }} data-testid="primary-pump-card">
            <div className="px-5 py-5" style={{ background: `linear-gradient(135deg, ${T.ink}, #2D2620)` }}>
              <span className="text-white/50 text-[10px] font-bold uppercase tracking-[0.2em] flex items-center gap-1.5 mb-2">
                <Check size={11} /> Recommended For You
              </span>
              <h2 className="text-white text-xl sm:text-2xl font-bold">{p.brand} {p.name}</h2>
              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                <span className="px-2.5 py-1 rounded-lg text-xs font-bold bg-white/10 text-white">{p.hp} HP</span>
                {p.stages && <span className="px-2 py-1 rounded-lg text-xs bg-white/10 text-white/80">{p.stages} stg</span>}
                <span className="px-2 py-1 rounded-lg text-xs bg-white/10 text-white/80 capitalize">{p.phase} Phase</span>
                {p.max_showers && <span className="px-2 py-1 rounded-lg text-xs bg-white/10 text-white/80">{p.max_showers} showers</span>}
                {p.max_pressure_bar && <span className="px-2 py-1 rounded-lg text-xs bg-white/10 text-white/80">{p.max_pressure_bar} bar</span>}
              </div>
            </div>
            <div className="px-5 py-2 flex items-center gap-2" style={{ backgroundColor: cf.bg }}>
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: cf.c }} />
              <span className="text-xs font-semibold" style={{ color: cf.c }}>{cf.l}</span>
            </div>
            {rec.why?.length > 0 && (
              <div className="px-5 py-4" style={{ backgroundColor: T.white }}>
                <h4 className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: T.ink3 }}>Why this pump?</h4>
                <ul className="space-y-2">
                  {rec.why.map((r, i) => (
                    <motion.li key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                      className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: '#FEE2E2' }}>
                        <Check size={11} style={{ color: T.red }} strokeWidth={3} />
                      </div>
                      <span className="text-sm leading-relaxed" style={{ color: T.ink2 }}>{r}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            )}
            {p.price && (
              <div className="px-5 py-3 border-t" style={{ borderColor: T.border, backgroundColor: T.cream }}>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-bold" style={{ color: T.ink }}>{"\u20B9"}{p.price.toLocaleString("en-IN")}</span>
                  <span className="text-xs" style={{ color: T.ink3 }}>approx.</span>
                </div>
                <p className="text-[11px] mt-0.5" style={{ color: T.ink3 }}>
                  {rec.price_note || "Indicative pricing. Final quote with accessories from our store."}
                </p>
              </div>
            )}
          </motion.div>

          {/* ALTERNATIVE (only in fallback view — top3 block already includes alt) */}
          {alt && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
              className="rounded-xl p-4" style={{ backgroundColor: T.white, border: `1px solid ${T.border}` }} data-testid="alt-pump-card">
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.ink3 }}>Also consider</span>
              <h4 className="font-bold mt-0.5" style={{ color: T.ink }}>{alt.brand} {alt.name}</h4>
              <div className="flex items-center gap-2 mt-1 text-sm" style={{ color: T.ink2 }}>
                <span>{alt.hp} HP</span>
                <span className="capitalize">{alt.phase} Phase</span>
                {alt.price && <span style={{ color: T.ink3 }}>{"\u20B9"}{alt.price.toLocaleString("en-IN")}</span>}
              </div>
            </motion.div>
          )}
        </>
      )}

      {/* ACCESSORIES */}
      {acc && (
        <div className="rounded-xl p-4 sm:p-5" style={{ backgroundColor: T.white, border: `1px solid ${T.border}` }} data-testid="accessories-section">
          <h4 className="text-[10px] font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: T.ink3 }}>
            <Package size={13} /> Recommended Accessories
          </h4>
          <ul className="space-y-2">
            {acc.items?.map((it, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm">
                <Check size={14} style={{ color: T.red }} className="mt-0.5 flex-shrink-0" />
                <span className="font-medium" style={{ color: T.ink2 }}>{it.name}</span>
              </li>
            ))}
          </ul>
          {acc.note && (
            <p className="text-[11px] mt-3 pt-2 border-t" style={{ color: T.ink3, borderColor: T.border }}>
              {acc.note}
            </p>
          )}
        </div>
      )}

      {/* LEAD */}
      {!leadSubmitted ? (
        <div className="rounded-xl p-5 shadow-sm" style={{ backgroundColor: T.white, border: `1px solid ${T.border}` }} data-testid="lead-capture-form">
          <h4 className="font-semibold mb-1 flex items-center gap-2" style={{ color: T.ink }}>
            <MessageSquare size={16} style={{ color: T.red }} /> Get this on WhatsApp
          </h4>
          <p className="text-sm mb-4" style={{ color: T.ink3 }}>Our dealer will send you a detailed quote.</p>
          {!showFull ? (
            <div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 z-10" style={{ color: T.ink3 }} />
                  <input type="tel" inputMode="numeric" pattern="[0-9]*" value={phone} onChange={(e) => onPhoneChange(e.target.value)}
                    data-testid="lead-phone-input" placeholder="10-digit mobile number" maxLength={10}
                    className="w-full pl-10 pr-4 py-3 rounded-lg text-sm outline-none focus:ring-2"
                    style={{ border: `1px solid ${phoneErr ? T.red : T.border}`, color: T.ink }} />
                </div>
                <button onClick={tryNext} disabled={!phoneValid}
                  data-testid="lead-next-btn"
                  className="px-5 py-3 text-white rounded-lg disabled:opacity-40" style={{ backgroundColor: T.red }}>
                  <ArrowRight size={18} />
                </button>
              </div>
              {phoneErr && (
                <p className="mt-1.5 text-xs" style={{ color: T.red }} data-testid="lead-phone-error">{phoneErr}</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm mb-1" style={{ color: T.ink2 }}>
                <Phone size={14} /> {phone}
                <button onClick={() => setShowFull(false)} className="text-xs hover:underline ml-auto" style={{ color: T.red }}>Change</button>
              </div>
              <div className="relative">
                <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 z-10" style={{ color: T.ink3 }} />
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} data-testid="lead-name-input"
                  placeholder="Your name *"
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-sm outline-none focus:ring-2" 
                  style={{ border: `1px solid ${T.border}`, color: T.ink }} />
              </div>
              <div className="relative">
                <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 z-10" style={{ color: T.ink3 }} />
                <input type="text" value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="lead-location-input"
                  placeholder="Area (optional)"
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-sm outline-none focus:ring-2"
                  style={{ border: `1px solid ${T.border}`, color: T.ink }} />
              </div>
              <button onClick={trySubmit}
                disabled={!name || !phoneValid || leadLoading} data-testid="lead-submit-btn"
                className="w-full py-3.5 text-white font-bold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2"
                style={{ backgroundColor: T.wa }}>
                {leadLoading ? <Loader2 size={18} className="animate-spin" /> : <MessageSquare size={18} />}
                Get Quote on WhatsApp
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl p-5" style={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0' }} data-testid="lead-success">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#D1FAE5' }}>
                <Check size={20} style={{ color: '#059669' }} />
              </div>
              <div className="flex-1">
                <h4 className="font-bold" style={{ color: '#065F46' }}>Your details have been saved!</h4>
                <p className="text-sm mt-1" style={{ color: '#047857' }}>We've received your enquiry. Our team will contact you shortly.</p>
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid #A7F3D0' }}>
                  <p className="text-xs" style={{ color: '#059669' }}>
                    Reference: <span className="font-mono font-bold">{refNum || 'Processing...'}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* WhatsApp Button - optional for faster response */}
          <div className="rounded-xl p-4" style={{ backgroundColor: T.warm, border: `1px solid ${T.border}` }}>
            <p className="text-sm mb-3" style={{ color: T.ink2 }}>Want a faster response? Send us a WhatsApp message:</p>
            {waUrl ? (
              <a href={waUrl} target="_blank" rel="noopener noreferrer"
                className="w-full py-3 text-white font-bold rounded-lg flex items-center justify-center gap-2"
                style={{ backgroundColor: T.wa }}
                data-testid="whatsapp-send-btn">
                <MessageSquare size={18} /> Send on WhatsApp
              </a>
            ) : (
              <a href={`https://wa.me/${dealer?.whatsapp || '919840834767'}?text=${encodeURIComponent('Hi, I need a pump recommendation. Reference: ' + (refNum || ''))}`}
                target="_blank" rel="noopener noreferrer"
                className="w-full py-3 text-white font-bold rounded-lg flex items-center justify-center gap-2"
                style={{ backgroundColor: T.wa }}
                data-testid="whatsapp-fallback-btn">
                <MessageSquare size={18} /> Send on WhatsApp
              </a>
            )}
          </div>
          
          {/* Other options */}
          <div className="rounded-xl p-4" style={{ backgroundColor: T.white, border: `1px solid ${T.border}` }} data-testid="closure-section">
            <div className="flex gap-2">
              <button onClick={() => window.location.reload()} 
                className="flex-1 py-2.5 text-sm font-medium rounded-lg transition-colors"
                style={{ border: `1px solid ${T.border}`, color: T.ink2 }}
                data-testid="start-new-btn">
                Find another pump
              </button>
              <a href={`tel:${dealer?.phone || '+919840834767'}`}
                className="flex-1 py-2.5 text-sm font-medium text-white rounded-lg flex items-center justify-center gap-1.5"
                style={{ backgroundColor: T.red }}
                data-testid="call-dealer-btn">
                <Phone size={14} /> Call us
              </a>
            </div>
          </div>
        </div>
      )}

      {/* DEALER CONTACT CARD — brand styled, always visible on result screen */}
      <DealerContactCard />
    </motion.div>
  );
}

function DealerContactCard() {
  return (
    <div
      className="rounded-xl overflow-hidden shadow-md"
      style={{ border: `1px solid #B22222` }}
      data-testid="dealer-contact-card"
    >
      <div className="px-4 py-3" style={{ backgroundColor: "#B22222" }}>
        <h3 className="text-white font-bold tracking-wide text-sm">Uzhavar Maiyyam · உழவர் மையம்</h3>
        <p className="text-white/80 text-[11px] mt-0.5">Authorised Texmo & Aquasub Dealer · Thanjavur</p>
      </div>
      <div className="px-4 py-4 space-y-3" style={{ backgroundColor: "#F5F1EA" }}>
        <div className="grid grid-cols-2 gap-2">
          <a href="tel:+919865868623"
            className="flex items-center justify-center gap-2 py-3 rounded-lg text-white font-semibold text-sm shadow"
            style={{ backgroundColor: "#B22222" }}
            data-testid="dealer-mobile-btn">
            <Phone size={14} /> 98658 68623
          </a>
          <a href="tel:+914362230883"
            className="flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm"
            style={{ backgroundColor: "#ffffff", color: "#B22222", border: "1px solid #B22222" }}
            data-testid="dealer-landline-btn">
            <Phone size={14} /> 04362-230883
          </a>
        </div>
        <a href="mailto:uzhavarmaiyyam@gmail.com"
          className="flex items-center gap-2 text-sm"
          style={{ color: "#3D3630" }}
          data-testid="dealer-email-link">
          <span style={{ color: "#B22222" }}>✉</span>
          <span className="underline decoration-dotted">uzhavarmaiyyam@gmail.com</span>
        </a>
        <div className="text-xs leading-relaxed" style={{ color: "#3D3630" }}>
          <div className="font-semibold mb-0.5" style={{ color: "#B22222" }}>Visit our store</div>
          209A/2972, Gandhiji Salai, Thanjavur - 613 001
        </div>
        <a
          href="https://www.google.com/maps/search/?api=1&query=Uzhavar+Maiyyam+Gandhiji+Salai+Thanjavur"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold"
          style={{ backgroundColor: "#ffffff", color: "#B22222", border: "1px solid #B22222" }}
          data-testid="dealer-maps-link">
          <MapPin size={14} /> Open in Google Maps
        </a>
      </div>
    </div>
  );
}

// =============================================
// LEAD GATE - Uzhavar Design
// =============================================
function LeadGate({ step, onSubmit, onSkip, loading: ll, language }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [loc, setLoc] = useState("");
  const [phoneErr, setPhoneErr] = useState("");

  const phoneValid = isValidPhone(phone);
  const onPhoneChange = (v) => {
    const digits = (v || "").replace(/\D/g, "").slice(0, 10);
    setPhone(digits);
    if (phoneErr) setPhoneErr("");
  };
  const trySubmit = () => {
    if (!name.trim()) return;
    if (!phoneValid) { setPhoneErr("Please enter a valid 10-digit mobile number"); return; }
    onSubmit({ phone, name, location: loc });
  };

  return (
    <motion.div variants={slide} initial="enter" animate="center" data-testid="lead-gate">
      <Pills reqs={step?.known_requirements} />
      <div className="rounded-xl p-6 shadow-sm mb-4" style={{ backgroundColor: T.white, border: `2px solid ${T.red}` }}>
        <div className="text-center mb-5">
          <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: '#FEE2E2' }}>
            <Check size={28} style={{ color: T.red }} />
          </div>
          <h3 className="text-lg font-semibold mb-1" style={{ color: T.ink }}>Your recommendation is ready!</h3>
          <p className="text-sm" style={{ color: T.ink3 }}>Share your details to see it and get a WhatsApp quote.</p>
        </div>
        <div className="space-y-3">
          <div className="relative"><User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 z-10" style={{ color: T.ink3 }} />
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} data-testid="gate-name-input"
              placeholder="Your name *" className="w-full pl-10 pr-4 py-3.5 rounded-lg text-sm outline-none focus:ring-2" 
              style={{ border: `1px solid ${T.border}`, color: T.ink }} /></div>
          <div>
            <div className="relative"><Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 z-10" style={{ color: T.ink3 }} />
              <input type="tel" inputMode="numeric" pattern="[0-9]*" value={phone} onChange={(e) => onPhoneChange(e.target.value)} data-testid="gate-phone-input"
                placeholder="10-digit mobile number *" maxLength={10} className="w-full pl-10 pr-4 py-3.5 rounded-lg text-sm outline-none focus:ring-2"
                style={{ border: `1px solid ${phoneErr ? T.red : T.border}`, color: T.ink }} /></div>
            {phoneErr && (
              <p className="mt-1.5 text-xs" style={{ color: T.red }} data-testid="gate-phone-error">{phoneErr}</p>
            )}
          </div>
          <div className="relative"><MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 z-10" style={{ color: T.ink3 }} />
            <input type="text" value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="gate-location-input"
              placeholder="Area (optional)" className="w-full pl-10 pr-4 py-3.5 rounded-lg text-sm outline-none focus:ring-2"
              style={{ border: `1px solid ${T.border}`, color: T.ink }} /></div>
          <button onClick={trySubmit} disabled={!name || !phoneValid || ll}
            data-testid="gate-submit-btn"
            className="w-full py-4 text-white font-bold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2 text-base shadow-md"
            style={{ backgroundColor: T.teal }}>
            {ll ? <Loader2 size={20} className="animate-spin" /> : <><MessageSquare size={18} /> Show My Recommendation</>}
          </button>
          {/* Skip button - nearly invisible, small text at bottom */}
          <div className="pt-4 mt-2 border-t" style={{ borderColor: T.border }}>
            <button onClick={onSkip} data-testid="gate-skip-btn"
              className="text-[10px] opacity-40 hover:opacity-60" style={{ color: T.ink3 }}>
              skip for now
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// =============================================
// MAIN
// =============================================
export default function PumpAdvisor() {
  const [phase, setPhase] = useState("entry");
  const [sid, setSid] = useState(null);
  const [lang, setLang] = useState("en");
  const [step, setStep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [text, setText] = useState("");
  const [dealer, setDealer] = useState(null);
  const [leadDone, setLeadDone] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [waUrl, setWaUrl] = useState("");
  const [refNum, setRefNum] = useState("");
  const [reqs, setReqs] = useState({});
  const [speechOk, setSpeechOk] = useState(false);
  const [voiceTxt, setVoiceTxt] = useState("");
  const [awaitVoice, setAwaitVoice] = useState(false);
  const [hist, setHist] = useState(0);

  const recRef = useRef(null);
  const contentRef = useRef(null);
  const recommendationTopRef = useRef(null);

  useEffect(() => { axios.get(`${API}/dealer`).then((r) => setDealer(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      setSpeechOk(true);
      const r = new SR(); r.continuous = false; r.interimResults = true;
      recRef.current = r;
      r.onresult = (e) => {
        const t = Array.from(e.results).map((x) => x[0].transcript).join("");
        setVoiceTxt(t);
        if (e.results[0].isFinal) { setText(t); setListening(false); }
      };
      r.onerror = () => setListening(false);
      r.onend = () => setListening(false);
    }
  }, []);

  useEffect(() => { if (recRef.current) recRef.current.lang = lang === "ta" ? "ta-IN" : "en-IN"; }, [lang]);
  useEffect(() => {
    setTimeout(() => contentRef.current?.scrollTo({ top: contentRef.current.scrollHeight, behavior: "smooth" }), 200);
  }, [step, loading]);
  useEffect(() => {
    if (awaitVoice && text && sid && !loading) { setAwaitVoice(false); sendFreeText(text); }
  }, [awaitVoice, text, sid, loading]);

  const startSession = useCallback(async (voice = false) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/advisor/start`, { language: lang });
      setSid(r.data.session_id); setStep(r.data.step);
      setReqs(r.data.step.known_requirements || {}); setPhase("advisor"); setHist(0);
      if (voice) { setAwaitVoice(true); startListening(); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [lang]);

  const handleSelect = async (qid, val) => {
    if (loading || !sid) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/advisor/respond`, { session_id: sid, question_id: qid, answer: val });
      setStep(r.data.step); setReqs(r.data.step.known_requirements || {}); setHist((h) => h + 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const goBack = async () => {
    if (loading || !sid) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/advisor/back`, { session_id: sid });
      setStep(r.data.step); setReqs(r.data.step.known_requirements || {});
      setHist((h) => Math.max(0, h - 1));
      if (r.data.step.phase === "welcome") setHist(0);
      setLeadDone(false); setWaUrl("");
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const sendFreeText = async (t) => {
    if (!t?.trim() || loading || !sid) return;
    setLoading(true); setText(""); setVoiceTxt("");
    try {
      const r = await axios.post(`${API}/advisor/respond`, { session_id: sid, free_text: t.trim() });
      setStep(r.data.step); setReqs(r.data.step.known_requirements || {}); setHist((h) => h + 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const submitLead = async ({ phone, name, location }) => {
    if (!sid) return;
    if (!isValidPhone(phone)) return; // validated in form, defensive
    setLeadLoading(true);
    try {
      const r = await axios.post(`${API}/advisor/lead/full`, { session_id: sid, phone, name: name || "", location: location || "" });
      setLeadDone(true);
      if (r.data.ref_number) setRefNum(r.data.ref_number);
      if (r.data.whatsapp_url) {
        setWaUrl(r.data.whatsapp_url);
        // Scroll to top so the success card + WhatsApp tap-to-open button is visible.
        // Do NOT auto-open window.open() — Safari/Android block async popups.
        setTimeout(() => {
          if (recommendationTopRef.current) {
            recommendationTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (e) { console.error(e); }
    setLeadLoading(false);
  };

  const gateSubmit = async ({ phone, name, location }) => {
    if (!sid) return;
    if (!isValidPhone(phone)) return; // validated in form, defensive
    setLeadLoading(true);
    try {
      const lr = await axios.post(`${API}/advisor/lead/full`, { session_id: sid, phone, name, location: location || "" });
      setLeadDone(true);
      if (lr.data.ref_number) setRefNum(lr.data.ref_number);
      if (lr.data.whatsapp_url) setWaUrl(lr.data.whatsapp_url);
      const rr = await axios.post(`${API}/advisor/respond`, { session_id: sid });
      setStep(rr.data.step); setReqs(rr.data.step.known_requirements || {});
      // Scroll content area to top to show recommendation
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
      }, 100);
      // Then smooth scroll to the ref if available
      setTimeout(() => {
        if (recommendationTopRef.current) {
          recommendationTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      // No auto window.open — user must tap the visible WhatsApp button.
    } catch (e) { console.error(e); }
    setLeadLoading(false);
  };

  const gateSkip = async () => {
    if (!sid || loading) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/advisor/respond`, { session_id: sid });
      setStep(r.data.step); setReqs(r.data.step.known_requirements || {});
    } catch (e) { console.error(e); }
    setLoading(false);
    // Scroll to top after animation completes using ref
    setTimeout(() => {
      if (recommendationTopRef.current) {
        recommendationTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 400);
  };

  const startListening = () => {
    if (!recRef.current || listening) return;
    try { recRef.current.lang = lang === "ta" ? "ta-IN" : "en-IN"; recRef.current.start(); setListening(true); setVoiceTxt(""); } catch (e) { console.error(e); }
  };
  const stopListening = () => { if (recRef.current && listening) { recRef.current.stop(); setListening(false); } };
  const reset = () => {
    setPhase("entry"); setSid(null); setStep(null); setLoading(false);
    setText(""); setVoiceTxt(""); setReqs({}); setLeadDone(false); setWaUrl(""); setAwaitVoice(false); setHist(0);
  };

  if (phase === "entry") {
    return <EntryScreen dealer={dealer} language={lang} setLanguage={setLang} onStart={() => startSession(false)}
      onStartVoice={() => startSession(true)} speechSupported={speechOk} />;
  }

  const isRec = step?.phase === "recommending";
  const isGate = step?.phase === "lead_gate";
  const isEsc = step?.escalate;
  const showInput = !isRec && !isEsc && !isGate;
  const canBack = hist > 0 && !isRec;
  const isCat = step?.question?.id === "category";

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ backgroundColor: T.cream }} data-testid="advisor-view">
      {/* HEADER */}
      <header className="backdrop-blur-md px-4 py-2.5 flex-shrink-0 z-20" style={{ backgroundColor: 'rgba(255,255,255,0.95)', borderBottom: `1px solid ${T.border}` }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-1">
            {canBack ? (
              <button onClick={goBack} disabled={loading} data-testid="back-btn"
                className="flex items-center gap-1 px-2.5 py-2 text-sm font-semibold rounded-lg disabled:opacity-40"
                style={{ color: T.ink2 }}>
                <ChevronLeft size={18} /> {lang === "ta" ? "பின்" : "Back"}
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <img src={LOGO_ROUND} alt="உழவர் மையம்" className="h-9 w-9 object-contain" />
                <img src={LOGO_NAME} alt="உழவர் மையம்" className="h-6 object-contain hidden sm:block" />
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setLang(lang === "en" ? "ta" : "en")} data-testid="toggle-lang-btn"
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg" style={{ color: T.ink3 }}>
              <Globe size={13} /> {lang === "en" ? "தமிழ்" : "EN"}
            </button>
            <button onClick={reset} data-testid="home-btn"
              className="flex items-center gap-1.5 px-2.5 py-2 text-sm font-medium rounded-lg" style={{ color: T.ink3 }}>
              <RotateCcw size={14} /> <span className="hidden sm:inline">{lang === "ta" ? "புதிதாக" : "Start over"}</span>
            </button>
          </div>
        </div>
      </header>

      {/* PROGRESS */}
      <div className="px-4 py-2 flex-shrink-0" style={{ backgroundColor: T.white, borderBottom: `1px solid ${T.border}` }}>
        <div className="max-w-2xl mx-auto">
          <ProgressBar pct={step?.progress?.percentage || 0} label={step?.progress?.label || ""} />
        </div>
      </div>

      {/* CONTENT */}
      <div ref={contentRef} className="flex-1 overflow-y-auto no-scrollbar">
        <div className="max-w-2xl mx-auto px-4 py-6">
          {/* Hide pills on recommendation page to show product first */}
          {!isCat && !isRec && <Pills reqs={reqs} />}

          <AnimatePresence mode="wait">
            {loading ? (
              <ThinkingIndicator key="loading" language={lang} />
            ) : step ? (
              <motion.div key={step?.question?.id || step?.phase || "s"}>
                {step.framing && !isGate && <Framing text={step.framing} />}

                {isGate && !isEsc && <LeadGate step={step} onSubmit={gateSubmit} onSkip={gateSkip} loading={leadLoading} language={lang} />}

                {isEsc && (
                  <div className="animate-fadeIn" data-testid="escalation-view">
                    <div className="rounded-xl p-5 mb-4" style={{ backgroundColor: T.warm, border: `1px solid ${T.border}` }}>
                      <div className="flex items-start gap-3">
                        <HelpCircle size={20} style={{ color: T.red }} className="mt-0.5" />
                        <div><h3 className="font-semibold mb-1" style={{ color: T.ink }}>Expert Needed</h3><p className="text-sm" style={{ color: T.ink2 }}>{step.message}</p></div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <a href={`https://wa.me/${dealer?.whatsapp || ""}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-3.5 text-white font-medium rounded-lg text-sm" style={{ backgroundColor: T.wa }}>
                        <MessageSquare size={16} /> WhatsApp</a>
                      <a href={`tel:${dealer?.phone || ""}`}
                        className="flex items-center justify-center gap-2 px-4 py-3.5 font-medium rounded-lg text-sm border-2" style={{ borderColor: T.red, color: T.red }}>
                        <Phone size={16} /> Call</a>
                    </div>
                  </div>
                )}

                {isRec && !isEsc && (
                  <Recommendation step={step} dealer={dealer} onLeadSubmit={submitLead}
                    leadSubmitted={leadDone} leadLoading={leadLoading} language={lang} refNum={refNum} waUrl={waUrl} scrollRef={recommendationTopRef} />
                )}

                {!isRec && !isEsc && !isGate && step.question && (
                  isCat
                    ? <CategorySelection question={step.question} onSelect={handleSelect} disabled={loading} />
                    : <QuestionCard question={step.question} reasoning={step.reasoning} onSelect={handleSelect} disabled={loading} />
                )}

                {!isRec && !isEsc && !isGate && !step.question && step.message && (
                  <div className="bg-white rounded-xl border border-slate-200 p-5"><p className="text-slate-700">{step.message}</p></div>
                )}
              </motion.div>
            ) : null}
          </AnimatePresence>

          {listening && voiceTxt && (
            <div className="mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-700 italic">{voiceTxt}</p>
            </div>
          )}
        </div>
      </div>

      {/* INPUT BAR */}
      {showInput && (
        <div className="flex-shrink-0 bg-white/90 backdrop-blur-md border-t border-slate-200 px-4 py-3">
          <div className="max-w-2xl mx-auto flex gap-2">
            <input type="text" value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !loading && sendFreeText(text)}
              placeholder={lang === "ta" ? "டைப் செய்யுங்கள்..." : "Type your requirement..."} disabled={loading}
              data-testid="text-input"
              className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-teal-200 disabled:bg-slate-50 bg-white" />
            <button onClick={() => sendFreeText(text)} disabled={loading || !text.trim()} data-testid="send-btn"
              className="px-3.5 py-3 text-white rounded-xl disabled:opacity-40 flex-shrink-0" style={{ backgroundColor: T.teal }}>
              <Send size={18} />
            </button>
          </div>
        </div>
      )}

      {/* MOBILE BOTTOM NAV — always visible on mobile during advisor */}
      {!showInput && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-slate-200 px-4 py-2.5 z-40">
          <div className="flex justify-between items-center">
            {canBack ? (
              <button onClick={goBack} disabled={loading} data-testid="mobile-back-btn"
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 rounded-lg">
                <ChevronLeft size={16} /> Back
              </button>
            ) : <div />}
            <button onClick={reset} data-testid="mobile-home-btn"
              className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-slate-500 bg-slate-100 rounded-lg">
              <RotateCcw size={14} /> Start over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
